import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Server, Trash2, PlayCircle, Pencil, Link2, RefreshCw } from 'lucide-react';

export default function WhatsAppProxies() {
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', host: '', port: 1080, proxy_type: 'socks5', username: '', password: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [integrations, setIntegrations] = useState([]);

  const fetchData = async () => {
    try {
      const [pRes, iRes] = await Promise.all([
        api.get('/tenant/whatsapp-proxies'),
        api.get('/tenant/integrations'),
      ]);
      setProxies(pRes.data);
      setIntegrations(iRes.data.filter(i => ['evolution_api', 'meta_whatsapp', 'twilio_whatsapp', 'gupshup'].includes(i.provider_key)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    if (!form.name || !form.host || !form.port) { toast.error('Nombre, host y puerto son requeridos'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/tenant/whatsapp-proxies/${editing.id}`, form);
        toast.success('Proxy actualizado');
      } else {
        await api.post('/tenant/whatsapp-proxies', form);
        toast.success('Proxy creado');
      }
      setShowForm(false); setEditing(null);
      setForm({ name: '', host: '', port: 1080, proxy_type: 'socks5', username: '', password: '', notes: '' });
      fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post(`/tenant/whatsapp-proxies/${id}/test`);
      toast[data.result === 'success' ? 'success' : 'error'](
        data.result === 'success' ? `Conectado (${data.latency_ms}ms)` : `Fallo: ${data.error}`
      );
      fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(null); }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este proxy?')) return;
    try { await api.delete(`/tenant/whatsapp-proxies/${id}`); toast.success('Proxy eliminado'); fetchData(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleAssign = async (proxyId, integrationId) => {
    try {
      await api.post(`/tenant/whatsapp-proxies/${proxyId}/assign/${integrationId}`);
      toast.success('Proxy asignado');
      fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <PantallaCargando />;

  if (showForm) return (
    <div data-testid="proxy-form">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A]">{editing ? 'Editar Proxy' : 'Nuevo Proxy'}</h1>
        <Button variant="outline" size="sm" onClick={() => { setShowForm(false); setEditing(null); }}>Cancelar</Button>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-1 block">Nombre *</label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Proxy US-1" data-testid="proxy-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-1 block">Tipo</label>
            <select className="w-full h-10 px-3 text-sm border border-[#E2E8F0] rounded-md" value={form.proxy_type} onChange={e => setForm(f => ({ ...f, proxy_type: e.target.value }))} data-testid="proxy-type">
              <option value="socks5">SOCKS5</option><option value="socks4">SOCKS4</option><option value="http">HTTP</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-1 block">Host *</label>
            <Input value={form.host} onChange={e => setForm(f => ({ ...f, host: e.target.value }))} placeholder="proxy.example.com" data-testid="proxy-host" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-1 block">Puerto *</label>
            <Input type="number" value={form.port} onChange={e => setForm(f => ({ ...f, port: parseInt(e.target.value) || 0 }))} data-testid="proxy-port" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-1 block">Usuario (opcional)</label>
            <Input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} data-testid="proxy-username" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-1 block">Contraseña (opcional)</label>
            <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} data-testid="proxy-password" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-[#64748B] mb-1 block">Notas</label>
          <textarea className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-md resize-none" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-[#E2E8F0]">
          <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#0F172A] text-white" data-testid="proxy-save-btn">
            {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear Proxy')}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="whatsapp-proxies-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">Servidores Proxy WhatsApp</h1>
          <p className="text-sm text-[#64748B] mt-1">{proxies.length} proxies configurados</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ name: '', host: '', port: 1080, proxy_type: 'socks5', username: '', password: '', notes: '' }); setShowForm(true); }}
          className="bg-[#0F172A] text-white" data-testid="create-proxy-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Proxy
        </Button>
      </div>

      {proxies.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-12 text-center">
          <Server className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">Sin proxies configurados</p>
        </div>
      ) : (
        <div className="space-y-3">
          {proxies.map(p => (
            <div key={p.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid={`proxy-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${p.status === 'active' ? 'bg-emerald-500' : p.status === 'error' ? 'bg-red-400' : 'bg-[#CBD5E1]'}`} />
                  <div>
                    <p className="text-sm font-semibold text-[#0F172A]">{p.name}</p>
                    <p className="text-xs text-[#64748B]">{p.proxy_type.toUpperCase()} — {p.host}:{p.port}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button variant="ghost" size="sm" onClick={() => handleTest(p.id)} disabled={testing === p.id} data-testid={`test-proxy-${p.id}`}>
                    {testing === p.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(p); setForm({ name: p.name, host: p.host, port: p.port, proxy_type: p.proxy_type, username: '', password: '', notes: p.notes || '' }); setShowForm(true); }}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} className="text-red-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              {p.last_test_message && (
                <p className="text-[11px] text-[#94A3B8] mt-2 ml-5">{p.last_test_message}</p>
              )}
              {integrations.length > 0 && (
                <div className="mt-3 pt-2 border-t border-[#F1F5F9] flex flex-wrap gap-2">
                  {integrations.map(i => (
                    <Button key={i.id} variant="outline" size="sm" onClick={() => handleAssign(p.id, i.id)}
                      className="text-xs h-7" data-testid={`assign-proxy-${p.id}-${i.id}`}>
                      <Link2 className="w-3 h-3 mr-1" /> {i.display_name || i.provider_name}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
